/**
 * 后台接口客户端。
 *
 * 与 apps/web 的 lib/api.ts 的区别：
 *   - token 存 `hew3.admin.token`。**必须与 C 端（`hew3.token`）区分**：
 *     两端在同一浏览器里共用 localStorage，同名 key 会让「员工 token 被成员端读取」
 *     （或反之），受众隔离（aud）就只落在服务端一侧，前端会出现难以排查的错乱登录态。
 *   - 请求走同源 `/api`，由 Vite 开发服务器代理到后端；不跨域，也不需要 CORS。
 *
 * 服务端的错误结构统一为 `{ error, message }`，这里抛出 ApiError 交给界面展示。
 * 403 是权限模型的正常结果（范围外成员 / 角色无权限点），不能被当作「系统故障」。
 */

/** 与 apps/web 的 `hew3.token` 刻意不同名。 */
const TOKEN_KEY = 'hew3.admin.token';

export type ScopeKey = 'all' | 'guardianship' | 'none';

export interface StaffBrief {
  id: string;
  no: string;
  name: string;
  role: string;
  roleName: string;
  scope: ScopeKey;
}

export interface StaffSession {
  staff: { id: string; no: string; name: string; role: string; roleName: string };
  scope: ScopeKey;
  /** 权限点（如 `member:read_health`）——导航与按钮的可见性据此决定，不硬编码角色 */
  permissions: string[];
  /** null 表示不限范围 */
  visibleMemberCount: number | null;
  duty: string;
  lastLoginAt: string | null;
}

export interface LoginSuccess {
  token: string;
  expiresInSeconds: number;
  staff: StaffBrief;
}

export interface NeedTotp {
  needTotp: true;
  message: string;
}

/**
 * 成员档案。**敏感字段是可选且可能整体不存在的** ——
 * 服务端按权限裁剪（core/rbac.ts 的 redactMemberFields），无权时字段直接不下发。
 * 前端因此不能用 `?? 0` 兜底：那会把「无权限」冒充成真实的 0 分。
 */
export interface MemberItem {
  id: string;
  no: string;
  name: string;
  level: string | null;
  city: string | null;
  enterpriseScore: number | null;
  openAlerts?: number;
  health_score?: number;
  health_grade?: string;
  bio_age?: number;
  organ_age?: number;
  wealth_score?: number;
  wealth_grade?: string;
  trust_plan?: string;
}

export interface MembersResult {
  scope: ScopeKey;
  items: MemberItem[];
}

/** 详情接口比列表多出会籍加入日期与守护小组人数（仅详情 SQL 里查）。 */
export interface MemberDetail extends MemberItem {
  join_date?: string | null;
  guardian_count?: number;
}

export interface MemberDetailResult {
  member: MemberDetail;
  visibleFields: { health: boolean; wealth: boolean };
}

export interface AlertItem {
  id: string;
  level: string;
  title: string;
  status: string;
  foundAt: string | null;
  confidence: number | null;
  member: { id: string; no: string; name: string };
}

export interface AlertsResult {
  scope: ScopeKey;
  items: AlertItem[];
}

export interface OverviewResult {
  members: { active: number; scope: ScopeKey };
  alerts: { danger: number; warn: number; info: number };
  reports: { parseFailed: number };
  /** 通道名如实上报（开发环境是 `console`：验证码只写日志，并未真的发出短信） */
  channels: { sms: string };
}

export interface RoleRow {
  role: string;
  name: string;
  scope: ScopeKey;
  duty: string;
  permissions: string[];
}

export interface PermissionsResult {
  roles: RoleRow[];
}

export interface DispatchResult {
  ok: boolean;
  alertId: string;
  dispatchedBy: string;
  note: string;
}

/**
 * AI 报告中心（后台）。
 *
 * 三条与「诚实性」直接相关的约定，界面必须照此呈现，不能自行改写：
 *   1. `generatedBy` 是**如实产出标识**（`sk-engine+rules` = 成员端实时算法组装，
 *      `sk-engine+seed` = 演示种子预生成）。**绝不可翻译成「AI 生成」「大模型生成」**；
 *   2. `confidence` 与 `reviewedAt` 可能是 `null` —— `null` 是「未评估 / 未复核」，
 *      不是 0，界面不得用 0 或占位数字冒充；
 *   3. 详情页的 `honesty.calibrated` 为 false 时必须显式提示「未校准」。
 */

/** 报告类型：与 `packages/shared` 的 reportCaliber.types 一一对应（名称由该处口径给出）。 */
export type ReportType = 'analysis' | 'alert' | 'plan';

/** 报告状态。`ready` 待复核 / `reviewing` 复核中 / `reviewed` 已复核 / `failed` 生成失败。 */
export type ReportStatus = 'ready' | 'reviewing' | 'reviewed' | 'failed';

/** 复核结论：pending 未复核 / approved 已通过 / revise 需修订。 */
export type ReviewDecision = 'pending' | 'approved' | 'revise';

export interface ReportReviewer {
  id: string;
  name: string;
  at: string;
  note: string;
}

export interface ReportListItem {
  id: string;
  reportNo: string;
  type: ReportType | string;
  /** 类型中文名（服务端口径），缺失时界面如实显示「未提供」 */
  typeName?: string;
  title: string;
  status: ReportStatus | string;
  reviewStatus: ReviewDecision | string;
  dataCut?: string;
  modelTag?: string;
  /** 如实产出标识，见上方第 1 条 */
  generatedBy?: string;
  confidence: number | null;
  summary?: string;
  freshness?: { state: 'current' | 'stale'; days: number; text: string; basis: string };
  reviewer?: ReportReviewer | null;
  generatedAt: string | null;
  memberNo?: string;
  memberName?: string;
}

export interface ReportsResult {
  scope: ScopeKey;
  items: ReportListItem[];
}

/** 结构化正文：字段可能因报告类型缺失或服务端未下发，一律按「未提供」呈现。 */
export interface ReportPayload {
  kind?: string;
  question?: string;
  sla?: string;
  meta?: {
    modelTag?: string;
    generatedBy?: string;
    confidence?: number | null;
    dataCut?: string;
    calibrated?: boolean;
    notice?: string;
  };
  alertSummary?: {
    total?: number;
    byLevel?: { level: string; label: string; count: number }[];
    maxConfidence?: number | null;
    earliestFoundAt?: string | null;
    narrative?: string;
  };
  openAlerts?: {
    id?: string;
    level?: string;
    title?: string;
    desc?: string;
    action?: string;
    confidence?: number | null;
    foundAt?: string | null;
    status?: string;
  }[];
  counterfactual?: {
    calibrated?: boolean;
    scenario?: string;
    months?: number;
    trajectory?: { month?: number; label?: string; H?: number; E?: number; W?: number; composite?: number }[];
  };
  honestNotes?: string[];
}

export interface ReportDetail {
  id: string;
  reportNo: string;
  type: ReportType | string;
  typeName?: string;
  title: string;
  status: ReportStatus | string;
  reviewStatus: ReviewDecision | string;
  dataCut?: string;
  modelTag?: string;
  generatedBy?: string;
  confidence: number | null;
  summary?: string;
  reviewer?: ReportReviewer | null;
  generatedAt: string | null;
  member?: { id: string; no: string; name: string };
  payload?: ReportPayload;
  /** 报告级诚实边界，由服务端 payload 与元信息汇总（界面直接呈现，不自行推断） */
  honesty?: {
    generatedBy?: string;
    generatedByNote?: string;
    calibrated?: boolean;
    calibratedNote?: string;
    missingData?: string[];
  };
}

export interface ReviewResult {
  ok: boolean;
  id: string;
  reportNo: string;
  status: string;
  reviewStatus: string;
  reviewer?: { id: string; no: string; name: string; at: string };
  note: string;
}

/** 待复核汇总：分组计数来自服务端，界面不得写死任何数字。 */
export interface PendingReportsSummary {
  scope: ScopeKey;
  visibleMemberCount: number | null;
  byReviewStatus: { reviewStatus: string; count: number }[];
}

/* ---------------- 邀约准入：邀请码管理 + 成员建档 ---------------- */

/**
 * 邀请码状态。
 *
 * 「已用完」与「已作废」在数据库里**同形**（作废的实现就是把 `max_uses` 置为 `used_count`），
 * 靠 `revoked_at` 区分。因此界面只认服务端算出的 `status` / `statusLabel`，
 * 不自己用 `usedCount >= maxUses` 去推断状态 —— 那会把一次人工撤销显示成自然耗尽。
 */
export type InviteStatus = 'available' | 'exhausted' | 'expired' | 'revoked';

export interface InviteItem {
  id: string;
  code: string;
  level: string;
  /** 中文会籍级别（服务端口径，界面不另维护映射表） */
  levelName: string;
  referrer: string;
  note: string;
  usedCount: number;
  maxUses: number;
  remaining: number;
  expiresAt: string | null;
  createdAt: string;
  revokedAt: string | null;
  expired: boolean;
  exhausted: boolean;
  revoked: boolean;
  status: InviteStatus | string;
  statusLabel: string;
}

export interface InvitesResult {
  /** 会籍级别选项由服务端下发 */
  levels: { key: string; name: string }[];
  boards: string[];
  summary: { total: number; available: number; exhausted: number; expired: number; revoked: number };
  items: InviteItem[];
}

export interface CreateInvitesInput {
  level: string;
  referrer?: string;
  note?: string;
  maxUses?: number;
  expiresAt?: string;
  count?: number;
  code?: string;
}

export interface CreateInvitesResult {
  ok: boolean;
  created: number;
  items: InviteItem[];
  /** 过期时间已过时的如实提醒（创建成功但状态就是「已过期」） */
  warning?: string;
}

export interface RevokeInviteResult {
  ok: boolean;
  item: InviteItem;
}

export interface CreateMemberInput {
  phone: string;
  name: string;
  level: string;
  city?: string;
  company?: string;
  referrer?: string;
  inviteCode?: string;
  boardStaffId?: string;
  board?: string;
}

export interface CreatedMember {
  id: string;
  no: string;
  name: string;
  level: string;
  levelName: string;
  city: string | null;
  company: string | null;
  referrer: string;
  inviteCode: string;
}

export interface CreateMemberResult {
  ok: boolean;
  member: CreatedMember;
  invite: InviteItem | null;
  guardianship: { staffId: string; staffNo: string; staffName: string; board: string } | null;
  note: string;
}

/** 建档表单的守护员工选项。只下发指派所需的最小字段（工号 / 姓名 / 角色名 / 可见范围）。 */
export interface StaffOption {
  id: string;
  no: string;
  name: string;
  roleName: string;
  scope: ScopeKey;
  guardianCount: number;
}

export interface StaffOptionsResult {
  items: StaffOption[];
  /** 守护板块选项（健康 / 企业 / 资产 / 商务） */
  boards: string[];
  /** 未指定板块时的默认值，由服务端给出，界面不猜 */
  defaultBoard: string;
}

/* ---------------- 权益核销（后台） ---------------- */

/**
 * 次数型权益的余额与核销（benefit.routes.ts 的后台分区）。
 *
 * 三条界面必须照做的约定：
 *   1. `remaining` **已过期时为 `null`**，不是 0 —— 过期与「用完」是两件事，
 *      把过期显示成「剩余 0 次」会让操作人以为额度被核销掉了；
 *   2. 倒计时 `daysLeft` 由服务端按 `valid_to` 算出（到期日当天为 0，已过期为负），
 *      界面不得用本地时间自算天数，否则跨时区、跨年就会显示错；
 *   3. 核销失败时服务端用 403（权限 / 守护关系）与 409（已过期 / 额度不足）区分原因，
 *      界面一律照实显示原文，不做乐观更新。
 */
export type BenefitStatus = 'active' | 'used_up' | 'expired';

export interface BenefitItem {
  /** 权益（目录）id：核销接口路径里的 `:benefitId` 用它 */
  id: string;
  /** 该成员的余额行 id */
  balanceId: string;
  code: string;
  groupKey: string;
  groupName: string;
  name: string;
  unit: string;
  /** 口径里的年度上限（目录值） */
  quota: number;
  /** 会籍标准是否包含（false = 增值服务，费用单独约定） */
  included: boolean;
  perYear: boolean;
  desc: string;
  total: number;
  used: number;
  /** 剩余可用次数；**已过期时为 null**（不是 0） */
  remaining: number | null;
  /** 算术余额（过期也保留，仅供对账） */
  balance: number;
  validFrom: string;
  validTo: string;
  /** 服务端按 valid_to 实时算出的剩余天数（到期日当天 = 0，已过期 = 负数） */
  daysLeft: number;
  expired: boolean;
  status: BenefitStatus | string;
}

export interface BenefitGroup {
  key: string;
  name: string;
  items: BenefitItem[];
  usedOfGroup: number;
  totalOfGroup: number;
  remainingOfGroup: number;
  expiredItems: number;
}

export interface EntitlementSummary {
  totalRemaining: number;
  totalQuota: number;
  totalUsed: number;
  includedItems: number;
  valueAddedItems: number;
  expiredItems: number;
  usedUpItems: number;
  validFrom: string;
  validTo: string;
  daysLeft: number;
  expired: boolean;
}

/** 核销流水：「次数去哪了」的答案。`staffId` 为空串表示成员自助核销。 */
export interface BenefitUsage {
  id: string;
  code: string;
  name: string;
  count: number;
  reason: string;
  at: string;
  balanceAfter: number;
  staffId: string;
}

export interface MemberBenefitsResult {
  member: { id: string; no: string; name: string; level: string };
  memberId: string;
  groups: BenefitGroup[];
  summary: EntitlementSummary;
  /** 清零规则与有效期口径的服务端原文（界面直接呈现，不改写） */
  resetNote: string;
  periodNote: string;
  usages: BenefitUsage[];
}

export interface ConsumeBenefitResult {
  ok: boolean;
  usageId: string;
  benefitId: string;
  code: string;
  name: string;
  unit: string;
  count: number;
  total: number;
  used: number;
  remaining: number;
  balanceAfter: number;
  validTo: string;
  daysLeft: number;
  reason: string;
  staffId: string;
  at: string;
  summary: EntitlementSummary;
}

/* ---------------- 埋点看板（后台） ---------------- */

export interface AnalyticsTotals {
  total: number;
  members: number;
  sessions: number;
}

export interface AnalyticsCount {
  name: string;
  count: number;
  lastSeen: string;
}

export interface AnalyticsRouteReadiness {
  route: string;
  samples: number;
  p50: number;
  p90: number;
  worst: number;
  within3s: number;
}

export interface AnalyticsFunnelStep {
  step: string;
  events: number;
  sessions: number;
}

export interface AnalyticsFunnel {
  funnel: string;
  steps: AnalyticsFunnelStep[];
}

/**
 * 埋点看板。**样本量与「样本是否足够」是接口专门返回的字段**
 * （`sufficientSample` 由服务端按样本量判定），界面不允许只显示一个漂亮的达标率
 * 而不显示样本量 —— 样本不足时结论不成立，这是平台的硬约束。
 */
export interface AnalyticsResult {
  windowDays: number;
  totals: AnalyticsTotals;
  counts: AnalyticsCount[];
  readiness: {
    /** 达标阈值（毫秒），由服务端给出，界面不写死 */
    thresholdMs: number;
    routes: AnalyticsRouteReadiness[];
    overall: { samples: number; within: number; rate: number; sufficientSample: boolean };
  };
  funnels: AnalyticsFunnel[];
  /** 免责声明原文（统计口径的限定条件），界面必须原样显示 */
  disclaimer: string;
}

/* ---------------- 资源准入审核（后台） ---------------- */

/**
 * 资源准入审核的后台字段（与 `apps/api/src/modules/resource.routes.ts` 一一对应）。
 *
 * 三条约定，界面必须照此呈现：
 *   1. **三级内容随接口下发**（`tiers`）：每级的 `label` / `scope`（形式审查 / 实质审查 /
 *      现场核验）/ `evidenceHint` 都来自 `core/resourceAdmission.ts` 的 `tierDefinitions`，
 *      界面不得自己维护一份三级文案；
 *   2. **能不能做的下一步由接口给出**：详情里的 `canReviewTiers` 是服务端用
 *      `assertTierProgress()` 逐个试出来的结果。界面只渲染它，**不自己推断「下一级是哪一级」**，
 *      否则「不能跳级」这条硬约束就会有一份会漂移的前端副本；
 *   3. **缺失不是 0**：`licenseNo` / `licenseExpiresAt` / `nextReviewAt` / 审核记录里的
 *      `evidence` / `note` 都可能是空串或 `null`（例如一级还没审），界面一律显示「未提供」
 *      或「未审核」，**绝不用 0 或空白冒充**。
 */
export type ResourceSubjectStatus =
  | 'draft'
  | 'tier1_passed'
  | 'tier2_passed'
  | 'admitted'
  | 'listed'
  | 'rejected'
  | 'suspended'
  | 'revoked';

/** 三级口径：`tier` 1–3，随列表与详情下发。 */
export interface ResourceTierDefinition {
  tier: number;
  label: string;
  /** 形式审查 / 实质审查 / 现场核验 */
  scope: string;
  evidenceHint: string;
}

/** 证照有效期三态：`none` = 未登记，**不是**「永久有效」。 */
export type LicenseState = 'none' | 'valid' | 'expired' | string;

export interface ResourceSubject {
  id: string;
  subjectType: string;
  /** 中文类型名（机构 / 专家），服务端下发 */
  subjectTypeLabel: string;
  name: string;
  category: string;
  categoryName: string;
  city: string;
  licenseNo: string;
  licenseExpiresAt: string | null;
  licenseState: LicenseState;
  licenseStateLabel: string;
  riskNote: string;
  status: ResourceSubjectStatus | string;
  statusLabel: string;
  /** 已完成到第几级（0–3），以及它的中文文案 */
  completedTiers: number;
  completedTierText: string;
  listingStatus: string;
  listed: boolean;
  listedAt: string | null;
  revokedAt: string | null;
  /** 可约 = 三级通过（admitted）或已上架（listed） */
  bookable: boolean;
  /** 尽调结论（服务端原文，界面不改写） */
  dueDiligence: string;
  nextReviewAt: string | null;
  daysUntilNextReview: number | null;
  reviewOverdue: boolean;
  reviewSoon: boolean;
  createdAt: string;
  updatedAt: string;
}

/** 列表的汇总计数：**全部来自接口**，界面不自行累加（否则筛选一变数字就在说假话）。 */
export interface ResourceSubjectSummary {
  total: number;
  admitted: number;
  listed: number;
  draft: number;
  rejected: number;
  suspended: number;
  revoked: number;
  reviewOverdue: number;
  reviewSoon: number;
}

export interface ResourceSubjectsResult {
  tiers: ResourceTierDefinition[];
  categories: { key: string; name: string }[];
  summary: ResourceSubjectSummary;
  items: ResourceSubject[];
}

/** 一条审核记录。`tier` 为 0 表示「持续动态评估 · 复评」，不占三级序位。 */
export interface ResourceAdmissionRecord {
  id: string;
  tier: number;
  tierLabel: string;
  decision: string;
  /** 通过 / 否决 / 需补充材料（服务端下发的中文） */
  decisionLabel: string;
  reviewerId: string;
  reviewerName: string;
  evidence: string;
  note: string;
  reviewedAt: string;
}

/** 某一级次的归并结果，供界面画进度；未做过的级次 `records` 为 0。 */
export interface ResourceTierProgress {
  tier: number;
  label: string;
  passed: boolean;
  rejected: boolean;
  records: number;
  lastReviewedAt: string | null;
}

export interface ResourceLinkedResource {
  id: string;
  name: string;
  category: string;
  institution: string;
  dueDiligence: string;
  bookable: boolean;
}

/**
 * 该主体名下已发生的预约（未取消）。
 *
 * **刻意不含成员姓名**：审核员的 scope 是 `none`，不接触成员个体数据 ——
 * 界面因此只显示工单标识、状态、资源名与时间，**不得自行补成员信息**。
 */
export interface PendingBookingItem {
  id: string;
  memberId: string;
  status: string;
  resourceName: string;
  createdAt: string;
}

export interface PendingBookings {
  /** 全部未取消预约的数量；`items` 由服务端截断，**不代表 total** */
  total: number;
  items: PendingBookingItem[];
}

export interface ResourceSubjectDetail {
  item: ResourceSubject;
  tiers: ResourceTierDefinition[];
  timeline: ResourceAdmissionRecord[];
  /** 一级能否通过的前置条件（证照齐备且在有效期），由服务端判定 */
  tier1Ready: { ready: boolean; reason: string };
  tierProgress: ResourceTierProgress[];
  resources: ResourceLinkedResource[];
  bookableResourceCount: number;
  pendingBookings: PendingBookings;
  /** 当前状态下**合法**的下一级（服务端用状态机逐级试出来的），界面照此渲染入口 */
  canReviewTiers: number[];
}

export interface CreateResourceSubjectInput {
  name: string;
  subjectType?: 'institution' | 'expert';
  category: string;
  city?: string;
  licenseNo?: string;
  licenseExpiresAt?: string | null;
  riskNote?: string;
  nextReviewAt?: string | null;
}

export interface CreateResourceSubjectResult {
  ok: boolean;
  item: ResourceSubject;
  /** 服务端给出的下一步（三级是固定顺序，不需要运营自己记） */
  nextStep: string;
}

export interface ReviewResourceSubjectInput {
  tier: number;
  decision: 'pass' | 'reject' | 'need_more';
  evidence?: string;
  note?: string;
}

export interface ReviewResourceSubjectResult {
  ok: boolean;
  item: ResourceSubject;
  admission: ResourceAdmissionRecord;
  nextStep: string;
}

export interface ListResourceSubjectResult {
  ok: boolean;
  item: ResourceSubject;
  linkedResources: number;
  bookableResourceCount: number;
  note: string;
}

export interface RevokeResourceSubjectResult {
  ok: boolean;
  item: ResourceSubject;
  /** 该主体已发生的预约（未取消）—— 清退前必须先处置的对象 */
  bookings: PendingBookings;
  affectedResources: string[];
  /** 是否还有未处置的预约（true 时服务端也要求先处置） */
  requireDisposal: boolean;
  note: string;
}

export interface ReassessResourceSubjectResult {
  ok: boolean;
  item: ResourceSubject;
  previousNextReviewAt: string | null;
  nextReviewAt: string;
  daysUntilNextReview: number | null;
  note: string;
}

/* ---------------- 官网咨询线索（后台） ---------------- */

export interface LeadItem {
  id: string;
  name: string;
  company: string;
  phone: string;
  interest: string;
  message: string;
  source: string;
  status: 'new' | 'contacted' | 'closed' | string;
  note: string;
  createdAt: string;
  updatedAt: string;
}

export interface LeadsResult {
  summary: { total: number; new: number; contacted: number; closed: number };
  items: LeadItem[];
}

export interface UpdateLeadResult {
  ok: boolean;
  item: LeadItem;
}

/* ---------------- token 存取 ---------------- */

export function getToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // 隐私模式下 localStorage 可能不可用，忽略即可（会话仍在内存中）
  }
}

/* ---------------- 错误 ---------------- */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let onUnauthorized: (() => void) | null = null;

/** 由 App 注册：token 失效（401）时自动回到登录页。 */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers['content-type'] = 'application/json';

  const response = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const err = (payload ?? {}) as { error?: string; message?: string; details?: unknown };
    if (response.status === 401) {
      setToken(null);
      onUnauthorized?.();
    }
    throw new ApiError(
      response.status,
      err.error ?? 'request_failed',
      err.message ?? `请求失败（${response.status}）`,
      err.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
};

/* ---------------- 后台接口 ---------------- */

/**
 * 员工登录。口令正确但该账号启用了 TOTP 而未传验证码时，
 * 服务端返回 **200** `{ needTotp: true }` —— 这不是失败，界面要切到验证码输入。
 */
export function staffLogin(input: { no: string; password: string; totp?: string }): Promise<LoginSuccess | NeedTotp> {
  return api.post<LoginSuccess | NeedTotp>('/api/admin/auth/login', input);
}

export function isNeedTotp(result: LoginSuccess | NeedTotp): result is NeedTotp {
  return 'needTotp' in result;
}

export const adminApi = {
  me: (signal?: AbortSignal) => api.get<StaffSession>('/api/admin/me', signal),
  permissions: (signal?: AbortSignal) => api.get<PermissionsResult>('/api/admin/permissions', signal),
  overview: (signal?: AbortSignal) => api.get<OverviewResult>('/api/admin/overview', signal),
  members: (keyword: string, limit: number, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (keyword.trim()) query.set('keyword', keyword.trim());
    query.set('limit', String(limit));
    return api.get<MembersResult>(`/api/admin/members?${query.toString()}`, signal);
  },
  member: (id: string, signal?: AbortSignal) =>
    api.get<MemberDetailResult>(`/api/admin/members/${encodeURIComponent(id)}`, signal),
  alerts: (level: string, limit: number, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (level) query.set('level', level);
    query.set('limit', String(limit));
    return api.get<AlertsResult>(`/api/admin/alerts?${query.toString()}`, signal);
  },
  dispatchAlert: (id: string, note: string) =>
    api.post<DispatchResult>(`/api/admin/alerts/${encodeURIComponent(id)}/dispatch`, { note }),
  /**
   * 报告列表。`status` / `type` 为空串表示不筛选 —— 与筛选控件「全部」一一对应，
   * 不传参数比传空值更贴近接口契约（服务端只接受枚举内的值）。
   */
  reports: (status: string, type: string, limit: number, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (status) query.set('status', status);
    if (type) query.set('type', type);
    query.set('limit', String(limit));
    return api.get<ReportsResult>(`/api/admin/reports?${query.toString()}`, signal);
  },
  report: (id: string, signal?: AbortSignal) =>
    api.get<ReportDetail>(`/api/admin/reports/${encodeURIComponent(id)}`, signal),
  /** 复核：需要 report:review 权限点且成员在可见范围内；缺一即 403（服务端原文照实呈现）。 */
  reviewReport: (id: string, decision: 'approved' | 'revise', note: string) =>
    api.post<ReviewResult>(`/api/admin/reports/${encodeURIComponent(id)}/review`, { decision, note }),
  /** 待复核汇总：计数由服务端按可见范围分组，界面不写死数字。 */
  pendingReports: (signal?: AbortSignal) =>
    api.get<PendingReportsSummary>('/api/admin/reports/summary/pending', signal),

  /* ---------------- 邀约准入 ---------------- */

  /** 邀请码列表 + 会籍级别选项 + 按状态分组的摘要（计数全部来自服务端）。 */
  invites: (signal?: AbortSignal) => api.get<InvitesResult>('/api/admin/invites', signal),
  /** 生成邀请码。`count` 最多 50；不传 `code` 由服务端生成并保证不重复。 */
  createInvites: (input: CreateInvitesInput) => api.post<CreateInvitesResult>('/api/admin/invites', input),
  /** 作废邀请码：`max_uses` 置为 `used_count`（剩余归零、不物理删除），并写入审计链。 */
  revokeInvite: (id: string) => api.post<RevokeInviteResult>(`/api/admin/invites/${encodeURIComponent(id)}/revoke`),
  /** 成员建档：必须给真实姓名（接口会覆盖「待完善档案」占位符）。 */
  createMember: (input: CreateMemberInput) => api.post<CreateMemberResult>('/api/admin/members', input),
  /** 建档表单的守护员工选项（需要 member:write）。 */
  staffOptions: (signal?: AbortSignal) => api.get<StaffOptionsResult>('/api/admin/staff/options', signal),
  logout: () => api.post<{ ok: boolean }>('/api/admin/auth/logout'),

  /* ---------------- 权益核销 ---------------- */

  /**
   * 成员权益总览（分组余额 + 清零倒计时 + 最近核销流水）。
   * 需要 `member:read` **且**该成员在守护关系内，缺一即 403（服务端原文照实呈现）。
   */
  memberBenefits: (memberId: string, signal?: AbortSignal) =>
    api.get<MemberBenefitsResult>(`/api/admin/members/${encodeURIComponent(memberId)}/benefits`, signal),
  /**
   * 员工代办核销。需要 `benefit:consume` + 守护关系；
   * 409 会明确区分「已过期」与「额度不足」，界面照实显示，不做乐观更新。
   */
  consumeBenefit: (memberId: string, benefitId: string, count: number, reason: string) =>
    api.post<ConsumeBenefitResult>(
      `/api/admin/members/${encodeURIComponent(memberId)}/benefits/${encodeURIComponent(benefitId)}/consume`,
      { count, reason },
    ),

  /* ---------------- 资源准入审核 ---------------- */

  /**
   * 被审主体列表。`status` / `tier` / `keyword` 为空表示不筛选
   * （`tier` 的语义是「已完成到该级次及以上」，由服务端过滤）。
   * 需要 `resource:approve`；缺该权限点的角色会拿到 403 原文。
   */
  resourceSubjects: (filter: { status: string; tier: string; keyword: string }, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (filter.status) query.set('status', filter.status);
    if (filter.tier) query.set('tier', filter.tier);
    if (filter.keyword.trim()) query.set('keyword', filter.keyword.trim());
    const suffix = query.toString();
    return api.get<ResourceSubjectsResult>(`/api/admin/resource-subjects${suffix ? `?${suffix}` : ''}`, signal);
  },
  /** 主体详情：三级时间线、可约相关事实与**合法可审级次**都由服务端给出。 */
  resourceSubject: (id: string, signal?: AbortSignal) =>
    api.get<ResourceSubjectDetail>(`/api/admin/resource-subjects/${encodeURIComponent(id)}`, signal),
  /** 新建待审主体：落库状态为 `draft`（未审核），因此不可约。 */
  createResourceSubject: (input: CreateResourceSubjectInput) =>
    api.post<CreateResourceSubjectResult>('/api/admin/resource-subjects', input),
  /** 三级审核。跳级、终态推进、一级证照不合格都由服务端拒绝（409），界面照实显示原文。 */
  reviewResourceSubject: (id: string, input: ReviewResourceSubjectInput) =>
    api.post<ReviewResourceSubjectResult>(`/api/admin/resource-subjects/${encodeURIComponent(id)}/review`, input),
  /** 上架：仅 `admitted` 可上架，其余状态服务端一律 409（界面不隐藏这个事实）。 */
  listResourceSubject: (id: string) =>
    api.post<ListResourceSubjectResult>(`/api/admin/resource-subjects/${encodeURIComponent(id)}/list`),
  /**
   * 暂停 / 清退。**返回该主体已发生的预约数量与列表**：
   * 一清了之会让成员手里已约好的号凭空消失，因此界面必须先把这份清单摆出来。
   */
  revokeResourceSubject: (id: string, mode: 'suspend' | 'revoke', reason: string) =>
    api.post<RevokeResourceSubjectResult>(`/api/admin/resource-subjects/${encodeURIComponent(id)}/revoke`, {
      mode,
      reason,
    }),
  /** 持续动态评估：记录一次复评并顺延下次复评时间。 */
  reassessResourceSubject: (
    id: string,
    input: { conclusion: 'ok' | 'issue'; note?: string; nextReviewAt?: string | null },
  ) =>
    api.post<ReassessResourceSubjectResult>(`/api/admin/resource-subjects/${encodeURIComponent(id)}/reassess`, input),

  /* ---------------- 埋点看板 ---------------- */

  /** 运营指标看板。需要 `analytics:read`；`days` 由服务端限定为 1–90（默认 7）。 */
  analytics: (days: number, signal?: AbortSignal) =>
    api.get<AnalyticsResult>(`/api/admin/analytics?days=${encodeURIComponent(String(days))}`, signal),

  /* ---------------- 官网咨询线索 ---------------- */

  /** 官网表单线索；`status` 为空表示全部。需要 `member:read`。 */
  leads: (status: string, limit: number, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (status) query.set('status', status);
    query.set('limit', String(limit));
    return api.get<LeadsResult>(`/api/admin/consultations?${query.toString()}`, signal);
  },
  /** 标记已联系 / 已关闭，并写跟进备注。需要 `member:write`。 */
  updateLead: (id: string, status: 'contacted' | 'closed' | 'new', note: string) =>
    api.post<UpdateLeadResult>(`/api/admin/consultations/${encodeURIComponent(id)}/status`, { status, note }),
};
